// Apolo IDE — LearningPanel Metrics sub-tab (WS L6.4 + WS L7).
//
// L7 enhancement: prefer the consolidated `/api/learning/metrics`
// endpoint and render the new outcomes_7d block (success rate, signal
// source breakdown, top-3 skills). When that endpoint is unavailable
// (older Worker, sandboxed test, offline) we fall back to the L6
// per-status aggregation so the panel never goes blank.

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  fetchCandidates,
  fetchSkills,
  fetchMetrics,
} from '../../../../hooks/useLearning.js';

const CANDIDATE_STATUSES = ['pending', 'approved', 'applied', 'failed'];

function pct(value) {
  if (!Number.isFinite(value)) return '0%';
  return `${Math.round(value * 100)}%`;
}

export default function Metrics() {
  const [metrics, setMetrics] = useState(null);
  const [legacyCounts, setLegacyCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const m = await fetchMetrics();
        if (m?.ok && m?.data?.candidates) {
          if (!cancelled) {
            setMetrics(m.data);
            setLegacyCounts(null);
          }
          return;
        }
        // L7 endpoint not available → fall back to the L6 per-status flow.
        const next = {};
        await Promise.all(
          CANDIDATE_STATUSES.map(async (status) => {
            const res = await fetchCandidates(status, { limit: 1 });
            next[status] = res.ok ? (res.data?.total ?? 0) : 0;
          }),
        );
        const skillsRes = await fetchSkills();
        next.skills_total = skillsRes.ok ? (skillsRes.data?.skills?.length ?? 0) : 0;
        if (!cancelled) {
          setLegacyCounts(next);
          setMetrics(null);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading && !metrics && !legacyCounts) {
    return (
      <div
        className="flex items-center gap-2 p-3 text-[11px] text-apolo-textTertiary"
        role="status"
      >
        <Loader2 size={12} className="animate-spin" aria-hidden="true" />
        Carregando métricas...
      </div>
    );
  }
  if (error) {
    return (
      <div role="alert" className="p-3 text-[11px] text-apolo-error">
        {error}
      </div>
    );
  }

  // ─── L7 path — full metrics with outcomes ────────────────────
  if (metrics) {
    const tiles = [
      { label: 'Pending', value: metrics.candidates.pending ?? 0, tone: 'text-amber-300' },
      { label: 'Approved', value: metrics.candidates.approved ?? 0, tone: 'text-blue-300' },
      { label: 'Applied', value: metrics.candidates.applied ?? 0, tone: 'text-emerald-300' },
      { label: 'Failed', value: metrics.candidates.failed ?? 0, tone: 'text-rose-300' },
      { label: 'Auto-skills', value: metrics.skills.total ?? 0, tone: 'text-violet-300' },
      { label: 'Disabled', value: metrics.skills.disabled ?? 0, tone: 'text-zinc-400' },
      { label: 'Nudges', value: metrics.nudges.pending ?? 0, tone: 'text-cyan-300' },
    ];
    const outcomes = metrics.outcomes_7d || { total: 0, success_rate: 0, by_skill: [] };
    const topSkills = (outcomes.by_skill || [])
      .slice()
      .sort((a, b) => (b.total || 0) - (a.total || 0))
      .slice(0, 3);

    return (
      <div className="p-3 space-y-3">
        <dl
          aria-label="Métricas do learning loop"
          className="grid grid-cols-2 gap-2"
        >
          {tiles.map((t) => (
            <div
              key={t.label}
              className="rounded border border-apolo-borderSubtle bg-apolo-surface2 p-2"
            >
              <dt className="text-[10px] uppercase tracking-wide text-apolo-textTertiary">
                {t.label}
              </dt>
              <dd className={`mt-1 text-lg font-semibold ${t.tone}`}>{t.value}</dd>
            </div>
          ))}
        </dl>

        <section
          aria-label="Outcomes nos últimos 7 dias"
          className="rounded border border-apolo-borderSubtle bg-apolo-surface2 p-2"
        >
          <header className="flex items-baseline justify-between">
            <h3 className="text-[10px] uppercase tracking-wide text-apolo-textTertiary">
              Outcomes ({outcomes.window_days || 7}d)
            </h3>
            <span className="text-xs font-medium text-apolo-textSecondary">
              {outcomes.total ?? 0} signals · success rate{' '}
              <span className="text-emerald-300 font-semibold">
                {pct(outcomes.success_rate)}
              </span>
            </span>
          </header>
          {topSkills.length === 0 ? (
            <p className="mt-2 text-[11px] text-apolo-textTertiary">
              Nenhum outcome registrado ainda. Use o thumbs-up/down após uma ativação de skill
              para alimentar o tracker.
            </p>
          ) : (
            <ul className="mt-2 space-y-1">
              {topSkills.map((s) => (
                <li
                  key={s.skill_id}
                  className="flex items-center justify-between text-[11px] text-apolo-textSecondary"
                >
                  <span className="truncate" title={s.skill_id}>
                    {s.skill_id.slice(0, 8)}…
                  </span>
                  <span>
                    {s.success}/{s.total}
                    <span className="ml-2 text-apolo-textTertiary">
                      {pct(s.success_rate)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    );
  }

  // ─── Legacy L6 fallback ──────────────────────────────────────
  const rows = [
    { label: 'Pending', value: legacyCounts?.pending ?? 0, tone: 'text-amber-300' },
    { label: 'Approved', value: legacyCounts?.approved ?? 0, tone: 'text-blue-300' },
    { label: 'Applied', value: legacyCounts?.applied ?? 0, tone: 'text-emerald-300' },
    { label: 'Failed', value: legacyCounts?.failed ?? 0, tone: 'text-rose-300' },
    { label: 'Auto-skills', value: legacyCounts?.skills_total ?? 0, tone: 'text-violet-300' },
  ];

  return (
    <dl aria-label="Métricas do learning loop" className="grid grid-cols-2 gap-2 p-3">
      {rows.map((r) => (
        <div
          key={r.label}
          className="rounded border border-apolo-borderSubtle bg-apolo-surface2 p-2"
        >
          <dt className="text-[10px] uppercase tracking-wide text-apolo-textTertiary">{r.label}</dt>
          <dd className={`mt-1 text-lg font-semibold ${r.tone}`}>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}
